import { useCallback, useRef, useState } from 'react';

import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';

import { SizedRelativeSpinner } from '@components/loading-spinner';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { IMAGE_EXTENSIONS } from '@utils/assets';
import { pxToRem } from '@utils/converters';
import { uuid } from '@utils/uuid';

import {
  getAiBuilderMessagesQueryKey,
  getAiBuilderSessionQueryKey,
  useAcceptPlan,
  useAIBuilderExamplePrompts,
  useAnswerQuestions,
  useDeleteAIBuilderHistory,
  useEnterPlanModeDecision,
  useInterruptAIBuilder,
  usePickResource,
  useRunAIBuilder,
  useUpdateSessionMode,
} from 'api/ai-builder/hooks';
import {
  AIBuilderMode,
  ChatSessionResponse,
  PaginatedMessagesResponse,
  QuestionAnswer,
} from 'api/ai-builder/requests';

import { ChatHeader, InputBox, MessagesContainer, WorkflowExamplesPanel } from './components';
import {
  useAIBuilderChat,
  useAIBuilderWebSocket,
  useAutoResizeTextarea,
  useChatScrollController,
  useFileUpload,
  type ChatMessage,
} from './hooks';

import { useNodes } from '../hooks/use-nodes';

const DEFAULT_INPUT_ROW_HEIGHT = 72;

export const AiBuilderChat = ({
  journeyShortId,
  onClose,
}: {
  journeyShortId: string;
  onClose?: () => void;
}) => {
  const {
    shortId,
    messages,
    clearChat,
    session,
    sessionReady,
    messagesReady,
    isInitialLoading,
    startRequest,
    endRequest,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAIBuilderChat({ journeyShortId });
  const queryClient = useQueryClient();
  const { t } = useScopedTranslate(locale);
  const { data: examplePrompts = [], isInitialLoading: isExamplePromptsLoading } =
    useAIBuilderExamplePrompts({ enabled: session?.hasMessages === false });
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const messagesInnerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const inputRowRef = useRef<HTMLDivElement | null>(null);
  const buttonsRowRef = useRef<HTMLDivElement | null>(null);
  const [chatInput, setChatInput] = useState('');
  const [inputRowHeight, setInputRowHeight] = useState(DEFAULT_INPUT_ROW_HEIGHT);
  const [isExamplesOpen, setIsExamplesOpen] = useState(false);
  const {
    uploadedFiles,
    fileError,
    handleFileSelected,
    handleRemoveFile,
    clearFiles: clearUploadedFiles,
    clearError: clearFileError,
    setError: setFileError,
  } = useFileUpload({
    allowedExtensions: IMAGE_EXTENSIONS,
    getErrorMessage: (extension) => t('onlyImagesSupported', { extension }),
  });

  const {
    onScroll: handleScroll,
    pauseAutoScroll,
    scrollToBottom,
  } = useChatScrollController({
    containerRef: messagesRef,
    contentRef: messagesInnerRef,
    messages,
    isReady: sessionReady && messagesReady,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    sessionKey: shortId ?? 'no-session',
  });

  const runPrompt = useRunAIBuilder();
  const interruptPrompt = useInterruptAIBuilder();
  const answerQuestionsMutation = useAnswerQuestions();
  const pickResourceMutation = usePickResource();
  const deleteHistoryMutation = useDeleteAIBuilderHistory();
  const updateModeMutation = useUpdateSessionMode();
  const acceptPlanMutation = useAcceptPlan();
  const enterPlanModeMutation = useEnterPlanModeDecision();
  const { selectedNode } = useNodes();

  const handleModeChange = useCallback(
    (newMode: AIBuilderMode) => {
      updateModeMutation.mutate({
        journeyShortId,
        mode: newMode,
      });
    },
    [updateModeMutation, journeyShortId]
  );

  const { clearActiveToolCalls } = useAIBuilderWebSocket({
    journeyShortId,
    shortId,
  });

  const handleInterruptRequest = useCallback(() => {
    if (!shortId) return;
    interruptPrompt.mutate({ journeyShortId, shortId });
    clearActiveToolCalls();
    endRequest();
  }, [interruptPrompt, journeyShortId, shortId, clearActiveToolCalls, endRequest]);

  const appendUserMessage = useCallback(
    (text: string, fileNames: string[] = []) => {
      if (!session || !shortId) return;
      const userMessage: ChatMessage = {
        id: uuid(),
        role: 'user',
        text,
        files: fileNames,
      };
      queryClient.setQueryData(
        getAiBuilderMessagesQueryKey(journeyShortId),
        (old: InfiniteData<PaginatedMessagesResponse> | undefined) => {
          if (!old || !old.pages.length) {
            const initialPage: PaginatedMessagesResponse = {
              shortId,
              mode: session.mode,
              messages: [userMessage],
              hasMore: false,
              oldestMessageId: null,
              isGenerating: false,
              loadingLabel: null,
            };
            return {
              pages: [initialPage],
              pageParams: [undefined],
            };
          }
          return {
            ...old,
            pages: old.pages.map((page, index) => {
              if (index === 0) {
                return {
                  ...page,
                  messages: [...page.messages, userMessage],
                };
              }
              return page;
            }),
          };
        }
      );
    },
    [queryClient, journeyShortId, shortId, session]
  );

  const handleSubmitAnswers = useCallback(
    async (answers: QuestionAnswer[]) => {
      if (!shortId) return;

      startRequest('Reasoning');
      answerQuestionsMutation.mutate(
        {
          journeyShortId,
          shortId,
          answers,
        },
        {
          onSuccess: (response) => {
            queryClient.setQueryData(
              getAiBuilderSessionQueryKey(journeyShortId),
              (old: ChatSessionResponse | undefined) => {
                if (!old) return old;
                return {
                  ...old,
                  isGenerating: response.isGenerating,
                  loadingLabel: response.loadingLabel,
                };
              }
            );
          },
          onError: () => {
            endRequest();
          },
        }
      );
    },
    [shortId, journeyShortId, answerQuestionsMutation, queryClient, startRequest, endRequest]
  );

  const handleAcceptPlan = useCallback(async () => {
    if (!shortId) return;

    startRequest('Building workflow');
    acceptPlanMutation.mutate(
      {
        journeyShortId,
        shortId,
      },
      {
        onSuccess: (response) => {
          queryClient.setQueryData(
            getAiBuilderSessionQueryKey(journeyShortId),
            (old: ChatSessionResponse | undefined) => {
              if (!old) return old;
              return {
                ...old,
                isGenerating: response.isGenerating,
                loadingLabel: response.loadingLabel,
              };
            }
          );
        },
        onError: () => {
          endRequest();
        },
      }
    );
  }, [shortId, journeyShortId, acceptPlanMutation, queryClient, startRequest, endRequest]);

  const handlePickResource = useCallback(
    async (selection: { id: string; name: string }) => {
      if (!shortId) return;

      startRequest('Reasoning');
      pickResourceMutation.mutate(
        {
          journeyShortId,
          shortId,
          resourceId: selection.id,
        },
        {
          onSuccess: (response) => {
            queryClient.setQueryData(
              getAiBuilderSessionQueryKey(journeyShortId),
              (old: ChatSessionResponse | undefined) => {
                if (!old) return old;
                return {
                  ...old,
                  isGenerating: response.isGenerating,
                  loadingLabel: response.loadingLabel,
                };
              }
            );
          },
          onError: () => {
            endRequest();
          },
        }
      );
    },
    [shortId, journeyShortId, pickResourceMutation, queryClient, startRequest, endRequest]
  );

  const handleApproveEnterPlanMode = useCallback(async () => {
    if (!shortId) return;

    enterPlanModeMutation.mutate({
      journeyShortId,
      shortId,
      decision: 'approved',
    });
  }, [shortId, journeyShortId, enterPlanModeMutation]);

  const handleRejectEnterPlanMode = useCallback(async () => {
    if (!shortId) return;

    enterPlanModeMutation.mutate({
      journeyShortId,
      shortId,
      decision: 'rejected',
    });
  }, [shortId, journeyShortId, enterPlanModeMutation]);

  const clearConversation = useCallback(async () => {
    await deleteHistoryMutation.mutateAsync(
      { journeyShortId },
      {
        onSuccess: () => {
          clearChat();
          clearUploadedFiles();
        },
      }
    );
  }, [deleteHistoryMutation, clearChat, clearUploadedFiles, journeyShortId]);

  const { textAreaRef, resizeTextarea } = useAutoResizeTextarea({
    containerRef: contentRef,
    inputRowRef: inputRowRef,
    buttonsRowRef: buttonsRowRef,
    inputValue: chatInput,
    onInputRowHeightChange: setInputRowHeight,
  });

  const handleTranscriptionComplete = useCallback(
    (text: string) => {
      const trimmedText = text.trimEnd();
      setChatInput((currentInput) =>
        currentInput ? `${currentInput} ${trimmedText}` : trimmedText
      );
      requestAnimationFrame(() => {
        resizeTextarea();
        textAreaRef.current?.focus();
      });
    },
    [resizeTextarea, textAreaRef]
  );

  const handleTranscriptionError = useCallback(
    (error: string) => {
      setFileError(error);
    },
    [setFileError]
  );

  const handleInputSubmit = useCallback(
    (value: string) => {
      setIsExamplesOpen(false);
      if (!session || !shortId) return;

      const files = uploadedFiles.map((item) => item.file);
      const fileNames = uploadedFiles.map((item) => item.file.name);

      appendUserMessage(value, fileNames);
      startRequest();

      runPrompt.mutate(
        {
          input: value,
          journeyShortId: journeyShortId,
          shortId,
          files,
          selectedAction: selectedNode
            ? { type: selectedNode.type, id: selectedNode.id }
            : undefined,
          mode: session.mode,
        },
        {
          onSuccess: (response) => {
            queryClient.setQueryData(
              getAiBuilderSessionQueryKey(journeyShortId),
              (old: ChatSessionResponse | undefined) => {
                if (!old) return old;
                return {
                  ...old,
                  isGenerating: response.isGenerating,
                  loadingLabel: response.loadingLabel,
                };
              }
            );
          },
          onError: () => {
            endRequest();
          },
        }
      );

      clearActiveToolCalls();

      setChatInput('');
      clearUploadedFiles();
      requestAnimationFrame(() => {
        scrollToBottom('auto');
        resizeTextarea();
        textAreaRef.current?.focus();
      });
    },
    [
      shortId,
      appendUserMessage,
      startRequest,
      runPrompt,
      journeyShortId,
      endRequest,
      resizeTextarea,
      textAreaRef,
      clearUploadedFiles,
      uploadedFiles,
      selectedNode,
      clearActiveToolCalls,
      session,
      queryClient,
      scrollToBottom,
    ]
  );

  const handleExamplePromptSelect = useCallback(
    (prompt: string) => {
      const trimmedPrompt = prompt.trim();
      if (!trimmedPrompt || (runPrompt.isLoading && !session?.isGenerating)) return false;

      setChatInput(trimmedPrompt);
      requestAnimationFrame(() => {
        resizeTextarea();
        textAreaRef.current?.focus();
      });

      return true;
    },
    [resizeTextarea, runPrompt.isLoading, session?.isGenerating, textAreaRef]
  );

  const handleOpenExamples = useCallback(() => {
    setIsExamplesOpen(true);
  }, []);

  const handleCloseExamples = useCallback(() => {
    setIsExamplesOpen(false);
  }, []);

  const handleSelectWorkflowExample = useCallback(
    (prompt: string) => {
      const hasStartedPrompt = handleExamplePromptSelect(prompt);
      if (!hasStartedPrompt) return;

      setIsExamplesOpen(false);
    },
    [handleExamplePromptSelect]
  );

  if (isInitialLoading || (session?.hasMessages === false && isExamplePromptsLoading)) {
    return (
      <PanelsContainer>
        <ChatPanel>
          <LoadingContainer>
            <SizedRelativeSpinner size={32} $skipMargin />
          </LoadingContainer>
        </ChatPanel>
      </PanelsContainer>
    );
  }

  if (!sessionReady || !messagesReady || !session) {
    return null;
  }

  return (
    <PanelsContainer>
      <ChatPanel>
        <ChatHeader
          mode={session.mode}
          onClose={onClose}
          onClearChat={clearConversation}
          journeyShortId={journeyShortId}
          isGenerating={session.isGenerating}
        />
        <Content ref={contentRef}>
          <MessagesContainer
            messages={messages}
            loadingLabel={session.loadingLabel}
            isBuilding={session.loadingLabel === 'Building workflow'}
            messagesRef={messagesRef}
            messagesInnerRef={messagesInnerRef}
            onScroll={handleScroll}
            onPauseAutoScroll={pauseAutoScroll}
            inputRowHeight={inputRowHeight}
            onSubmitAnswers={handleSubmitAnswers}
            onPickResource={handlePickResource}
            onAcceptPlan={handleAcceptPlan}
            onApproveEnterPlanMode={handleApproveEnterPlanMode}
            onRejectEnterPlanMode={handleRejectEnterPlanMode}
            onSelectExamplePrompt={handleExamplePromptSelect}
            onOpenExamples={handleOpenExamples}
            examplePrompts={examplePrompts}
            isPlanLoading={acceptPlanMutation.isLoading}
            isModeLoading={enterPlanModeMutation.isLoading}
          />
        </Content>
        <InputBox
          value={chatInput}
          onChange={setChatInput}
          onSubmit={handleInputSubmit}
          isDisabled={runPrompt.isLoading && !session.isGenerating}
          isGenerating={session.isGenerating}
          onInterrupt={handleInterruptRequest}
          textAreaRef={textAreaRef}
          inputRowRef={inputRowRef}
          buttonsRowRef={buttonsRowRef}
          onResize={resizeTextarea}
          files={uploadedFiles}
          onFileSelected={handleFileSelected}
          onFileRemove={handleRemoveFile}
          errorText={fileError || undefined}
          onClearError={clearFileError}
          selectedNode={selectedNode}
          onTranscriptionComplete={handleTranscriptionComplete}
          onTranscriptionError={handleTranscriptionError}
          mode={session.mode}
          onModeChange={handleModeChange}
        />
      </ChatPanel>
      <WorkflowExamplesPanel
        isOpen={isExamplesOpen}
        examplePrompts={examplePrompts}
        onClose={handleCloseExamples}
        onSelectExamplePrompt={handleSelectWorkflowExample}
      />
    </PanelsContainer>
  );
};

const Content = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minHeight: 0,
  width: '100%',
  position: 'relative',
});

const PanelsContainer = styled.div({
  height: '100%',
  width: '100%',
  position: 'relative',
});

const ChatPanel = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  background: theme.color.backgroundLightDark,
  overflow: 'hidden',
  position: 'relative',
  borderRadius: pxToRem(12),
  border: `1px solid ${theme.color.border10}`,
  boxShadow: theme.shadow.medium,
});

const LoadingContainer = styled.div({
  flex: '1 1 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const locale = {
  en: {
    onlyImagesSupported: 'Only images are supported. {extension} is not supported.',
  },
};
